import { Layers, Info, ExternalLink, Mail, Settings2 } from "lucide-react";
import { modules } from "@/data/modules";
import { ModuleCard } from "@/components/modules/ModuleCard";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function MyModules() {
  const { user, subscription } = useAuth();
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchModules = async () => {
      const { data } = await supabase
        .from("user_modules")
        .select("module_id")
        .eq("user_id", user.id);
      setActiveIds(data?.map((r) => r.module_id) ?? []);
      setLoading(false);
    };
    fetchModules();
  }, [user]);

  const activeModules = modules.filter((m) => activeIds.includes(m.id));
  const inactiveModules = modules.filter((m) => !activeIds.includes(m.id));

  const hasActiveSub = subscription.subscribed;

  if (loading) {
    return (
      <div className="container py-8 max-w-5xl">
        <p className="text-muted-foreground">Module werden geladen…</p>
      </div>
    );
  }

  // Build mailto link for change requests
  const changeMailto = `mailto:support@nxtinvoice.de?subject=${encodeURIComponent(
    "Modul-Änderung"
  )}&body=${encodeURIComponent(
    `Hallo NextInvoice-Team,\n\nich möchte meine Modulkonfiguration ändern.\n\nAktuelle Module: ${
      activeModules.length > 0
        ? activeModules.map((m) => m.name).join(", ")
        : "Keine"
    }\n\nGewünschte Änderung:\n[Bitte beschreiben]\n\nVielen Dank!`
  )}`;

  return (
    <div className="container py-8 max-w-5xl space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground mb-1">Meine Module</h1>
          <p className="text-muted-foreground">Übersicht Ihrer gebuchten Konfiguration.</p>
        </div>
        {activeModules.length > 0 && (
          <Button variant="outline" size="sm" asChild>
            <a href={changeMailto}>
              <Mail className="h-4 w-4 mr-2" />
              Konfiguration ändern
            </a>
          </Button>
        )}
      </div>

      {activeModules.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center space-y-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Layers className="h-6 w-6 text-muted-foreground" />
          </div>

          {hasActiveSub ? (
            <>
              <h2 className="font-display text-lg font-semibold text-foreground">
                Ihre Module werden eingerichtet
              </h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Sie haben ein aktives Abonnement. Ihre Module werden gerade für Sie
                konfiguriert und sind in Kürze verfügbar. Falls die Anzeige nach 24 Stunden
                noch leer ist, kontaktieren Sie bitte den Support.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button variant="outline" asChild>
                  <a href="mailto:support@nxtinvoice.de?subject=Module%20nicht%20sichtbar">
                    <Mail className="h-4 w-4 mr-2" />
                    Support kontaktieren
                  </a>
                </Button>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-display text-lg font-semibold text-foreground">
                Noch keine Module gebucht
              </h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Konfigurieren Sie Ihr individuelles ERP-System mit unserem Baukasten.
              </p>
              <Button asChild>
                <Link to="/konfigurator">
                  <Settings2 className="h-4 w-4 mr-2" />
                  Zum Konfigurator
                </Link>
              </Button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-muted/30 p-3 flex items-start gap-2 text-sm text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Ihre Module werden in der NextInvoice-Anwendung verwaltet. Hier
              sehen Sie Ihre gebuchte Konfiguration. Änderungen an Modulen können
              Sie über den Button „Konfiguration ändern" beantragen.
            </span>
          </div>

          <div>
            <h2 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Layers className="h-4 w-4" /> Aktiv ({activeModules.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {activeModules.map((m) => (
                <ModuleCard key={m.id} module={m} selected disabled />
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
              Verfügbare Module ({inactiveModules.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {inactiveModules.map((m) => (
                <ModuleCard key={m.id} module={m} disabled />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
